import { Vue } from 'vue-property-decorator';

import { Point, GeoJSONObject } from '@turf/helpers';
import {
  GeoJSONSourceRaw,
  Layer,
  Marker,
  Popup,
  MapSourceDataEvent,
  VectorSource,
  AnySourceData,
  AnyPaint
} from 'mapbox-gl';

import TargomoService from '@/services/targomo.service';
import { MapStore } from '@/store/map';
import {
  free4G,
  free5G,
  fibre,
  emploi,
  bouygues4G,
  SFR4G,
  orange4G,
  free4GIsohrone,
  bouygues4GIsohrone,
  SFR4GIsohrone,
  orange4GIsohrone
} from '@/services/internet4G&4G.servie';
// @ts-ignore
import Network from '@/assets/icons/Network.svg';
import { pointsToGeoJson } from '@/services/map.service';
import { AirTable } from '@/models/air-table.model';
import { PointType } from '@/models/point-type.model';
import { WidgetConfig } from '@/models/widget-config.model';
import ApiService from '@/services/api.service';
import { getModule } from 'vuex-module-decorators';
import store from '@/store';
import { projectDefaultColor } from '@/services/utils.service';

export abstract class WidgetGeneric extends Vue {
  protected _mapStore!: MapStore;
  protected _points!: Map<string, { data: AirTable[]; visibility: boolean }>;
  protected _widgetType!: string;
  protected _config!: WidgetConfig;

  protected _addedMarkersReference!: ({ sourceId, isSourceLoaded, ...rest }: MapSourceDataEvent) => void;
  protected _updateMarkersReference!: () => void;

  abstract created(): void;
  abstract beforeDestroy(): void;

  protected _beforeDestroy(): void {
    this._mapStore.removeMarker({ type: this._widgetType });
    this._mapStore.removeLayer({ type: this._widgetType });
    this._mapStore.removeSource({ type: this._widgetType });
    this._mapStore.setSelectedPopupEmploi(null);
    this._mapStore.removeLayer({ type: 'JOBS' });
    this._mapStore.removeSource({ type: 'JOBS' });
    this._removePopups();
    this._removeEmploiPopup();
    this._mapStore.setRecruitmentPoolWidget(false);

    if (this._addedMarkersReference) this._mapStore.map.off('data', this._addedMarkersReference);
    if (this._updateMarkersReference) this._mapStore.map.off('moveend', this._updateMarkersReference);
  }

  protected _getPoints(type: string): Promise<void> {
    this._mapStore.setLoading(true);
    return ApiService.getPoints(type).then((response: Record<string, any>) => {
      this._mapStore.setLoading(false);
      const data = response.data.map((fields: Record<string, any>) => ({ ...fields, type }));
      this._points.set(type, { data, visibility: true });
    });
  }
  protected _addGreenSpaceLegend(type: string): Promise<void> {
    return new Promise(resolve => {
      this._mapStore.setLoading(true);
      const images: any = [
        {
          fields: {
            lat: 48.77218427046083,
            lng: 2.2669518544918654,
            image: [{ url: 'https://dl.airtable.com/.attachmentThumbnails/c144ddcf954b0cdd311796565349c525/b5d433e2' }]
          }
        },
        {
          fields: {
            lat: 48.76973876913588,
            lng: 2.2971625887232623,
            image: [{ url: 'https://dl.airtable.com/.attachmentThumbnails/007d809a834930a4e99bf2d13703cc98/378b6524' }]
          }
        },
        {
          fields: {
            lat: 48.7741462252807,
            lng: 2.272285757840341,
            image: [{ url: 'https://dl.airtable.com/.attachmentThumbnails/a5250a21c31ca4172597a69e50bbacf0/e4f8faa6' }]
          }
        },
        {
          fields: {
            lat: 48.77535450420287,
            lng: 2.2997018442373056,
            image: [{ url: 'https://dl.airtable.com/.attachmentThumbnails/6656198266eeab5c92f84325601ba03b/eead78eb' }]
          }
        }
      ];
      const data = images.map(({ fields }: any) => ({ ...fields, type }));
      this._points.set(type, { data, visibility: true });
      this._mapStore.setLoading(false);
      this._addToMap();
      resolve(data);
    });
  }

  protected _addToMap(): void {
    this._mapStore.removeLayer({ type: this._widgetType });
    this._mapStore.removeSource({ type: this._widgetType });

    const points = [...this._points.values()]
      .filter(({ visibility }) => visibility)
      .map(({ data }) => data)
      .flat(1);
    const geoJsonPoints = pointsToGeoJson(points);

    const source: GeoJSONSourceRaw = {
      type: 'geojson',
      data: geoJsonPoints,
      cluster: true,
      clusterMaxZoom: 15,
      clusterRadius: 50
    };
    this._mapStore.addSource({ source, type: this._widgetType });
    const layer: Layer = {
      id: this._widgetType,
      source: this._widgetType,
      type: 'circle',
      paint: {
        'circle-radius': 0 // hide point
      }
    };
    this._mapStore.addLayer({ layer, type: this._widgetType });
    if (this._updateMarkersReference) {
      this._mapStore.map.off('moveend', this._updateMarkersReference);
    }
    this._addedMarkersReference = this._addedMarkers.bind(this, this._widgetType);
    this._mapStore.map.on('data', this._addedMarkersReference);
  }
  protected _addToMapFromAirQuality(): void {
    this._mapStore.removeLayer({ type: this._widgetType });
    this._mapStore.removeSource({ type: this._widgetType });
    const source: VectorSource = {
      type: 'vector',
      url: 'mapbox://maplabtech.7mk4dkl6'
    };
    const layer: Layer = {
      id: this._widgetType,
      type: 'fill',
      source: this._widgetType,
      'source-layer': 'AirQualityIndex-07122020_8am_-3bda77',
      layout: {},
      paint: {
        'fill-color': [
          'step',
          ['get', 'Indice'],
          `rgba(34, 184, 179, 0.6)`,
          0,
          `rgba(99, 192, 130, 0.6)`,
          20,
          `rgba(187, 207, 76, 0.6)`,
          30,
          `rgba(179, 204, 79, 0.6)`,
          50,
          `rgba(238, 194, 9, 0.6)`,
          80,
          `rgba(233, 0, 0, 0.6)`,
          95,
          `rgba(152, 0, 22, 0.6)`
        ]
      }
    };
    this._mapStore.addSource({ source, type: this._widgetType });
    this._mapStore.addLayer({ layer, type: this._widgetType });
  }
  protected _addToMapFromHeatMap(): void {
    this._mapStore.removeLayer({ type: this._widgetType });
    this._mapStore.removeSource({ type: this._widgetType });
    const source: AnySourceData = {
      type: 'image',
      url: './HeatMap.png',
      coordinates: [
        [1.9049832, 49.0181251],
        [2.708023, 49.0181251],
        [2.7080429, 48.6405139],
        [1.9049894, 48.6405239]
      ]
    };
    const layer: Layer = {
      id: this._widgetType,
      type: 'raster',
      paint: { 'raster-opacity': 0.25 },
      source: this._widgetType
    };
    this._mapStore.addSource({ source, type: this._widgetType });
    this._mapStore.addLayer({ layer, type: this._widgetType });
  }
  protected _addToMapFromMapbox(): void {
    this._mapStore.removeLayer({ type: this._widgetType });
    this._mapStore.removeSource({ type: this._widgetType });

    const source: VectorSource = {
      type: 'vector',
      url: 'mapbox://maplabtech.cet86w1a'
    };

    const layer: Layer = {
      id: this._widgetType,
      type: 'fill',
      source: this._widgetType,
      'source-layer': 'Noise_data_VS-766pqn',
      layout: {},
      paint: {
        'fill-color': [
          'step',
          ['get', 'gridcode'],
          `rgba(8, 249, 12, 0.25)`,
          5,
          `rgba(69, 187, 72, 0.25)`,
          7,
          `rgba(131, 125, 132, 0.25)`,
          8,
          `rgba(192, 64, 192, 0.25)`,
          9,
          `rgba(82, 10, 150, 0.25)`
        ],
        'fill-opacity': 1
      }
    };

    this._mapStore.addSource({ source, type: this._widgetType });
    this._mapStore.addLayer({ layer, type: this._widgetType });
  }

  protected _addToMapFromMapboxPopulation(): void {
    TargomoService.getDataMap().then(response => {
      const colors = ['#feebe2', '#fcc5c0', '#fa9fb5', '#f768a1', '#dd3497', '#ae017e', '#7a0177'];

      // create color breakpoints from metadata
      const breakpoints = response.populationMeta.breakpoints.kmeans.c7.map((b, i) => [b, colors[i]]);

      // build color definition
      const colorDef: any = ['interpolate', ['linear'], ['get', '0'], ...breakpoints.flat()];

      const layer: Layer = {
        id: 'statistics',
        type: 'fill',
        source: {
          type: 'vector',
          tiles: [response.tileRoute],
          minzoom: 10
        },
        'source-layer': '123', // source-layer is the statistic group id
        layout: {},
        paint: {
          'fill-opacity': 0.5,
          'fill-color': colorDef,
          'fill-outline-color': 'rgba(255,255,255,.5)'
        }
      };

      this._mapStore.addLayer({ layer, type: 'ECONOMIC_ECOSYSTEM_MULTIPOLYGON' });
    });
  }

  protected emploiPopup(e: any): void {
    const mapStore = getModule(MapStore, store);
    const data = e['features'][0].properties;
    mapStore.setSelectedPopupEmploi(data);
  }

  protected defaulteEmploiPopup(): void {
    const mapStore = getModule(MapStore, store);
    mapStore.setSelectedPopupEmploi('laMeuse');
  }

  protected internetFibrePopup(e: any): void {
    const mapStore = getModule(MapStore, store);
    mapStore.setSelectedPopup(
      new Popup({ offset: 5, maxWidth: '400px', className: 'routes-popup' }).setLngLat(e.lngLat).setHTML(
        `<div class="popup__content" style="font-weight: bold; display: flex; flex-direction: column; text-align: center">
          <div>${e['features'][0].properties.NOM_COM}</div>
          <div>${e['features'][0].properties.couv} %</div>
        </div>`
      )
    );
  }

  protected _addNoiseLevelIsohrone(type: any): void {
    this._mapStore.setLoading(true);
    ApiService.getGeoJson(type).then((response: any) => {
      const source: GeoJSONSourceRaw = {
        type: 'geojson',
        data: response.data
      };

      const layer: Layer = {
        id: type,
        type: 'fill',
        source: type,
        layout: {},
        paint: {
          'fill-color': [
            'match',
            ['get', 'LEGENDE'],
            '55',
            '#fffb01',
            '60',
            '#ff8f04',
            '65',
            '#ff2600',
            '70',
            '#dc34d0',
            '75',
            '#991b6b',
            '#fffb01'
          ],
          'fill-opacity': 0.4
        }
      };
      this._mapStore.addSource({ source, type });
      this._mapStore.addLayer({ layer, type });
      this._mapStore.setLoading(false);
    });
  }

  protected _addMapIsohrone(type: any): void {
    let data: any;
    let paint: AnyPaint;

    switch (type) {
      case 'internet-fibre':
        data = JSON.parse(fibre());
        paint = {
          'fill-color': [
            'match',
            ['get', 'couv'],
            0,
            '#b8bdca',
            5,
            '#b8bdca',
            10,
            '#99c5d3',
            25,
            '#85c5c0',
            50,
            '#60efbd',
            80,
            '#7af073',
            '#313b54'
          ],
          'fill-opacity': 0.5
        };
        break;
      case 'JOBS':
        data = JSON.parse(emploi());
        paint = {
          'fill-color': projectDefaultColor(),
          'fill-opacity': 0.3,
          'fill-outline-color': projectDefaultColor()
        };
        break;
    }
    const source: GeoJSONSourceRaw = {
      type: 'geojson',
      data
    };
    const layer: Layer = {
      id: type,
      type: 'fill',
      source: type,
      paint
    };
    this._mapStore.addSource({ source, type });
    this._mapStore.addLayer({ layer, type });
    if (type === 'internet-fibre') {
      this._mapStore.map.on('click', type, this.internetFibrePopup);
    } else if (type === 'JOBS') {
      this._mapStore.map.on('click', this.defaulteEmploiPopup);
      this._mapStore.map.on('click', type, this.emploiPopup);
    }
  }

  protected _removeEmploiPopup(): void {
    const mapStore = getModule(MapStore, store);
    mapStore.setSelectedPopup(null);
    mapStore.map.off('click', 'JOBS', this.emploiPopup);
    mapStore.map.off('click', this.defaulteEmploiPopup);
  }

  protected _removePopups(): void {
    const mapStore = getModule(MapStore, store);
    mapStore.setPointsOfInterestPopup(null);
    mapStore.setRestaurantsGitesGuestroomsShopsPopup(null);
    mapStore.setCoworkingsPopup(null);
    mapStore.setHotelsPopup(null);
    mapStore.setActivitiesPopup(null);
  }

  protected _removeInternetFibrePopup(): void {
    const mapStore = getModule(MapStore, store);
    mapStore.setSelectedPopup(null);
    mapStore.map.off('click', 'internet-fibre', this.internetFibrePopup);
  }

  protected _addToMapInternet4G5G(type: string, iconType: string, isohroneType?: string): void {
    const img = new Image(20, 20);
    if (!this._mapStore.map.hasImage('network')) {
      img.onload = () => this._mapStore.map.addImage('network', img);
      img.src = Network;
    }
    let sourceData: string;
    let sourceDataIsohrone: string;
    switch (type) {
      case 'FreeMobile5G':
        sourceData = JSON.parse(free5G());
        break;
      case 'free-mobile':
        sourceData = JSON.parse(free4G());
        sourceDataIsohrone = JSON.parse(free4GIsohrone());
        break;
      case 'bouygues':
        sourceData = JSON.parse(bouygues4G());
        sourceDataIsohrone = JSON.parse(bouygues4GIsohrone());
        break;
      case 'sfr':
        sourceData = JSON.parse(SFR4G());
        sourceDataIsohrone = JSON.parse(SFR4GIsohrone());
        break;
      case 'orange':
        sourceData = JSON.parse(orange4G());
        sourceDataIsohrone = JSON.parse(orange4GIsohrone());
        break;
    }
    const source: GeoJSONSourceRaw = {
      type: 'geojson',
      data: sourceData
    };
    const layer: Layer = {
      id: type,
      type: 'circle',
      source: type,
      paint: {
        'circle-color': type === 'FreeMobile5G' ? '#ea580c' : '#f7961d',
        'circle-radius': 10,
        'circle-stroke-color': '#fff',
        'circle-stroke-width': 2
      }
    };

    const sourceIsohrone: GeoJSONSourceRaw = {
      type: 'geojson',
      data: sourceDataIsohrone
    };
    const layerIsohrone: Layer = {
      id: isohroneType,
      type: 'fill',
      source: isohroneType,
      layout: {},
      paint: {
        'fill-color': '#f7961d',
        'fill-opacity': 0.5
      }
    };

    const sourceIcon: GeoJSONSourceRaw = {
      type: 'geojson',
      data: sourceData
    };
    const layerIcon: Layer = {
      id: iconType,
      type: 'symbol',
      source: iconType,
      layout: {
        'icon-image': 'network',
        'icon-size': 0.6
      }
    };

    if (type !== 'FreeMobile5G') {
      this._mapStore.addSource({ source: sourceIsohrone, type: isohroneType });
      this._mapStore.addLayer({ layer: layerIsohrone, type: isohroneType });
    }

    this._mapStore.addSource({ source, type });
    this._mapStore.addLayer({ layer, type });

    this._mapStore.addSource({ source: sourceIcon, type: iconType });
    this._mapStore.addLayer({ layer: layerIcon, type: iconType });
  }
  protected _addToMapFromMapboxGreenSpaces(): void {
    const source: VectorSource = {
      type: 'vector',
      url: 'mapbox://maplabtech.dt1okmg3'
    };

    const layer: Layer = {
      id: 'QUALITY_OF_LIFE_MULTIPOLYGON',
      type: 'fill',
      source: 'QUALITY_OF_LIFE_MULTIPOLYGON',
      'source-layer': 'Espaces_verts_VS-cplpqi',
      layout: {},
      paint: {
        'fill-color': [
          'match',
          ['get', 'fclass'],
          'cemetery',
          `rgba(214, 148, 50,  0.5)`,
          'park',
          `rgba(111, 206, 126, 0.5)`,
          'grass',
          `rgba(111, 206, 126, 0.5)`,
          'forest',
          `rgba(26, 99, 17, 0.5)`,
          'nature_reserve',
          `rgba(26, 99, 17, 0.5)`,
          `rgba(26, 99, 17, 0.5)`
        ],
        'fill-opacity': 1
      }
    };
    this._mapStore.addSource({ source, type: 'QUALITY_OF_LIFE_MULTIPOLYGON' });
    this._mapStore.addLayer({ layer, type: 'QUALITY_OF_LIFE_MULTIPOLYGON' });
  }

  protected _addToMapFromMapboxCumulative(): void {
    this._mapStore.removeLayer({ type: this._widgetType });
    this._mapStore.removeSource({ type: this._widgetType });

    const source: VectorSource = {
      type: 'vector',
      url: 'mapbox://maplabtech.aqw3tzdo'
    };

    const layer: Layer = {
      id: this._widgetType,
      type: 'fill',
      source: this._widgetType,
      'source-layer': 'Cumul_nuisances3-3drvq4',
      layout: {},
      paint: {
        'fill-color': [
          'step',
          ['get', 'nb_nuis_po'],
          `rgba(255, 255, 178, 0.25)`,
          0,
          `rgba(255, 255, 113, 0.25)`,
          1,
          `rgba(254, 166, 73, 0.25)`,
          2,
          `rgba(230, 49, 35, 0.25)`,
          3,
          `rgba(232, 60, 47, 0.25)`
        ]
      }
    };

    this._mapStore.addSource({ source, type: this._widgetType });
    this._mapStore.addLayer({ layer, type: this._widgetType });
  }

  private _addedMarkers(type: string, { sourceId, isSourceLoaded, coord }: MapSourceDataEvent): void {
    if (sourceId !== this._widgetType) return;

    if (!coord) return;

    if (!isSourceLoaded) return;

    this._mapStore.map.off('data', this._addedMarkersReference);
    // first event after features is loaded on map
    this._updateMarkers();
    this._updateMarkersReference = this._updateMarkers.bind(this, type);
    // // change data when user move or zoom
    this._mapStore.map.on('moveend', this._updateMarkersReference);
  }

  private _updateMarkers(): void {
    const features = this._mapStore.map.querySourceFeatures(this._widgetType);
    this._mapStore.removeMarker({ type: this._widgetType });

    const checkDuplication = new Set();
    features.forEach(({ geometry, properties }) => {
      const popupType: Record<string, any> = {};
      if (properties.content) {
        popupType.type = JSON.parse(properties.content).type;
        popupType.id = properties.id;
      }
      if (!geometry || !properties) return;

      if (!WidgetGeneric.isPoint(geometry)) return;

      const [lng, lat] = geometry.coordinates;

      const key = `${lng}-${lat}`;
      // check for duplication duplications
      if (checkDuplication.has(key)) return;

      checkDuplication.add(key);

      if (properties.cluster) {
        const count = properties.point_count;
        this._cluster(this._setMarker())({ lng, lat, count });
      } else {
        const content = JSON.parse(properties.content);
        this._marker(popupType, this._setMarker())({ lng, lat, name, ...content });
      }
    });
  }

  private _cluster(fn: (marker: Marker) => void): (params: { lng: number; lat: number; count: number }) => void {
    return ({ lng, lat, count }) => {
      const el = document.createElement('div');
      el.className = `environment__cluster`;
      el.innerText = count.toString();
      const marker = new Marker(el).setLngLat([lng, lat]);
      fn(marker);
    };
  }

  private _marker(obj: any, fn: (marker: Marker) => void) {
    return (point: PointType) => {
      const { lng, lat, name, type, website, description, surface } = point;
      const data = this._config[type];
      const markerElement = data.marker(point);
      const marker: any = new Marker({ element: markerElement }).setLngLat([lng, lat]);
      const formattedAddress = point.formatted_address;
      const phoneNumber = point.phone_number;
      const typeDisplay = point.type_display;
      const starts = point.category;
      let category: string;
      let popupContent: string;
      const adresse = `<div class="popup__category">
                        <div class="popup__description-type popup__description">
                          Adresse : 
                        </div>
                        <div
                          style="font-weight: 500; text-align: end" class="popup__description">
                          ${formattedAddress}
                        </div> 
                      </div>`;
      const webAddress = `${
        website
          ? `<div class="popup__category">
              <div class="popup__description-type popup__description">
                  Site Web :
              </div>
              <a
                style="font-weight: 500; text-align: end"
                class="popup__description"
                target="_blank"
                href="${website.includes('https://') || website.includes('http://') ? website : 'https://' + website}"
                >${website}
              </a>
            </div>`
          : ''
      }`;
      const adressePhoneWebsite = `${adresse}
                    ${
                      phoneNumber
                        ? `
                    <div class="popup__category">
                      <div class="popup__description-type popup__description">
                        Téléphone : 
                      </div>
                      <div style="font-weight: 500;" class="popup__description">
                        ${phoneNumber}
                      </div>
                    </div>`
                        : ''
                    }
                    ${webAddress}`;
      switch (type) {
        case 'supermarkets':
          category = 'Supermarchés';
          popupContent = `<div style="margin-top: 5px">${adresse}${webAddress}</div>`;
          break;
        case 'entertainment-places':
          category = 'Lieux de sortie';
          popupContent = `<div style="margin-top: 5px">${adresse}${webAddress}</div>`;
          break;
        case 'generalist':
          category = 'Médecins généralistes';
          popupContent = ``;
          break;
        case 'specialist':
          category = 'Médecins spécialistes';
          popupContent = ``;
          break;
        case 'dentist':
          category = 'Dentistes';
          popupContent = ``;
          break;
        case 'hospitals':
          category = 'Cliniques & hôpitaux';
          popupContent = `<div style="margin-top: 5px">${adressePhoneWebsite}</div>`;
          break;
        case 'pharmacies':
          category = 'Pharmacies';
          popupContent = `<div style="margin-top: 5px">${adressePhoneWebsite}</div>`;
          break;
        case 'nurseries':
          category = 'Crèches';
          popupContent = `<div style="margin-top: 5px">
                            ${adresse}
                            ${
                              phoneNumber
                                ? `
                            <div class="popup__category">
                              <div class="popup__description-type popup__description">
                                Téléphone : 
                              </div>
                              <div style="font-weight: 500;" class="popup__description">
                                ${phoneNumber}
                              </div>
                            </div>`
                                : ''
                            }
                          </div>`;
          break;
        case 'primaire':
          category = 'Primaires';
          break;
        case 'college':
          category = 'Collèges';
          popupContent = `<div style="margin-top: 5px">${adresse}</div>`;
          break;
        case 'lycee':
          category = 'Lycées';
          popupContent = `<div style="margin-top: 5px">${adresse}</div>`;
          break;
        case 'charging-stations':
          category = 'Bornes de recharge véh. éléctriques';
          popupContent = `<div style="margin-top: 5px">
                            ${adresse}
                            <div class="popup__category">
                              <div class="popup__description-type popup__description">
                                Descriptif :
                              </div>
                              <div style="font-weight: 500; text-align: end" class="popup__description">
                                ${description}
                              </div>
                            </div>
                          </div>`;
          break;
        case 'big-enterprises':
          category = 'Grandes entreprises';
          popupContent = `<div style="margin-top: 5px">${adresse}</div>`;
          break;
        case 'small-medium-enterprises':
          category = 'PME';
          popupContent = `<div style="margin-top: 5px">${adresse}</div>`;
          break;
        case 'real-estate-agencies':
          category = 'Agence immobilière';
          popupContent = `<div style="margin-top: 5px">${adressePhoneWebsite}</div>`;
          break;
        case 'lands':
          category = 'Offre foncière';
          popupContent = `<div style="margin-top: 5px">
                            ${adresse}
                            ${
                              typeDisplay
                                ? `
                            <div class="popup__category">
                              <div class="popup__description-type popup__description">
                                Type :
                              </div>
                              <div style="font-weight: 500;" class="popup__description">
                                ${typeDisplay}
                              </div>
                            </div>`
                                : ''
                            }
                            ${
                              surface
                                ? `
                            <div class="popup__category">
                              <div class="popup__description-type popup__description">
                                Surface :
                              </div>
                              <div style="font-weight: 500;" class="popup__description">
                                ${surface} m²
                              </div>
                            </div>`
                                : ''
                            }
                          </div>`;
          break;
        default:
      }
      const popupHeader = `<div>
                              <h3 style="font-size: 14px; font-weight: 500; margin-bottom: 8px" class="popup__title color">
                                  ${category}
                              </h3>
                              ${
                                type !== 'charging-stations' && name
                                  ? `
                              <h3 style="font-size: 20px; font-weight: bold;" class="popup__title color">
                                  ${name}
                              </h3>`
                                  : ''
                              }
                             </div>`;
      const popup = new Popup({ offset: 25, maxWidth: '400px', className: 'popup' }).setHTML(
        `<div class="popup__content"><div>${popupHeader}${popupContent}</div></div>`
      );
      if (
        ![
          'points-of-interest',
          'hotels',
          'restaurants',
          'gites',
          'guest-rooms',
          'shops',
          'schools',
          'doctors',
          'coworkings',
          'activities'
        ].includes(obj.type) &&
        window.innerWidth > 750
      ) {
        marker.setPopup(popup);
      }
      fn(marker);

      marker.getElement().addEventListener('click', () => {
        if (
          ![
            'points-of-interest',
            'generalist',
            'specialist',
            'dentist',
            'coworkings',
            'hotels',
            'restaurants',
            'gites',
            'guest-rooms',
            'shops',
            'schools',
            'doctors',
            'primaire',
            'college',
            'lycee',
            'activities'
          ].includes(obj.type)
        ) {
          this._mapStore.setMobilePopup(`<div class="popup__content"><div>${popupHeader}${popupContent}</div></div>`);
        } else {
          let api = 'schools';
          if (['generalist', 'specialist', 'dentist'].includes(obj.type)) {
            api = 'doctors';
          } else if (['primaire', 'college', 'lycee'].includes(obj.type)) {
            api = 'schools';
          } else if (['restaurants', 'gites', 'guest-rooms', 'shops'].includes(obj.type)) {
            api = obj.type;
            const categoryType: Record<string, string> = {
              restaurants: 'Cafés / Restaurants',
              gites: 'Gîtes',
              'guest-rooms': "Chambres d'hôtes",
              shops: 'Commerces, producteurs locaux'
            };
            category = categoryType[obj.type];
            if (window.innerWidth > 750) {
              ApiService.getPopupInfo(obj.type, obj.id).then(response => {
                this._mapStore.setRestaurantsGitesGuestroomsShopsPopup(response.data);
              });
              return;
            }
          } else if (obj.type === 'coworkings') {
            api = 'coworkings';
            category = 'Coworking';
            if (window.innerWidth > 750) {
              ApiService.getPopupInfo(obj.type, obj.id).then(response => {
                this._mapStore.setCoworkingsPopup(response.data);
              });
              return;
            }
          } else if (obj.type === 'hotels') {
            api = 'hotels';
            category = 'Hôtel';
            if (window.innerWidth > 750) {
              ApiService.getPopupInfo(obj.type, obj.id).then(response => {
                this._mapStore.setHotelsPopup(response.data);
              });
              return;
            }
          } else if (obj.type === 'points-of-interest') {
            api = 'points-of-interest';
            category = 'Nature et visites';
            if (window.innerWidth > 750) {
              ApiService.getPopupInfo(api, obj.id).then(response => {
                this._mapStore.setPointsOfInterestPopup(response.data);
              });
              return;
            }
          } else if (obj.type === 'activities') {
            api = 'activities';
            category = 'Activités';
            if (window.innerWidth > 750) {
              ApiService.getPopupInfo(api, obj.id).then(response => {
                this._mapStore.setActivitiesPopup(response.data);
              });
              return;
            }
          }
          ApiService.getPopupInfo(api, obj.id).then(response => {
            const imageCredit = `${
              response.data.photo_url
                ? `<div>
                      <img style="width: 100%; border-radius: 20px; max-height: 500px" alt="Photo de couverture" src="${response.data.photo_url}">
                      <div style="text-align: right;
                        position: relative;
                        bottom: 26px;
                        right: 12px;
                        color: white;
                        font-family: Montserrat, serif;
                        font-size: 10px;"
                        v-if="response.data.photo_credit"  
                      >
                          <span style="background-color: rgba(0, 0, 0, 0.65);
                            border-radius: 20px">
                            &nbsp;${response.data.photo_credit}&nbsp;
                          </span>
                        </div>
                    </div>`
                : ''
            }`;
            const categoryName = `${
              response.data.name
                ? `<div>
                      <h3 style="font-size: 14px; font-weight: 500; margin-bottom: 8px" class="popup__title color">${category}</h3>
                      <h3 style="font-size: 20px; font-weight: bold;" class="popup__title color">${response.data.name}</h3>
                    </div>`
                : ''
            }`;
            const adresse = `${
              response.data.formatted_address
                ? `
                <div class="popup__category">
                  <div style="font-weight: bold; margin-right: 3px; min-width: 67px;" class="popup__description">
                   Adresse :
                  </div>
                  <div
                   style="font-weight: 500; text-align: end" class="popup__description">
                   ${response.data.formatted_address}
                  </div>
                </div>`
                : ''
            }`;
            const speciality = `${
              response.data.speciality
                ? `
              <div class="popup__category">
                <div class="popup__description-type popup__description">
                  Spécialité :
                </div>
                <div style="font-weight: 500;" class="popup__description">
                  ${response.data.speciality}
                </div>
              </div>`
                : ''
            }`;
            const phoneNumber = `${
              response.data.phone_number
                ? `
              <div class="popup__category">
                <div class="popup__description-type popup__description">
                  Téléphone :
                </div>
                <div style="font-weight: 500;" class="popup__description">
                  ${response.data.phone_number}
                </div>
              </div>`
                : ''
            }`;
            const webSite = `${
              response.data.website
                ? `
              <div class="popup__category">
                <div class="popup__description-type popup__description">
                  Site Web :
                </div>
                <a
                  style="
                      font-weight: 500;
                      text-align: end; 
                      margin: 5px 0;
                      font-size: 14px;
                      color: #5e5e5e;
                      font-family: Montserrat, serif"
                  target="_blank"
                  href="${
                    response.data.website.includes('https://')
                      ? response.data.website
                      : 'https://' + response.data.website
                  }"
                >${response.data.website}</a>
              </div>`
                : ''
            }`;
            let popupBody = `<div class="popup__content">
                                ${imageCredit}
                                ${categoryName}
                                <div style="margin-top: 5px">${adresse}${speciality}</div>
                                <div>${phoneNumber}</div>
                              </div>`;
            if (type === 'hotels') {
              popupBody = `<div class="popup__content">
                            ${categoryName}
                            <div style="margin-top: 5px">${adresse}</div>
                            ${phoneNumber}
                            ${webSite}
                            ${
                              response.data.ranking
                                ? `
                                <div class="popup__category">
                                  <div class="popup__description-type popup__description">
                                    Classement :
                                  </div>
                                  <div style="font-weight: 500; text-align: end" class="popup__description">
                                    ${response.data.ranking}
                                  </div>
                                </div>`
                                : ''
                            }
                          </div>`;
            } else if (['restaurants', 'gites', 'guest-rooms', 'shops'].includes(obj.type)) {
              popupBody = `<div class="popup__content">
                            ${imageCredit}
                            ${categoryName}
                            <div style="margin-top: 5px">${adresse}</div>
                            ${phoneNumber}
                            ${webSite}
                          </div>`;
            } else if (obj.type === 'coworkings') {
              popupBody = `<div class="popup__content">
                            ${imageCredit}
                            ${categoryName}
                            <div style="margin-top: 5px">${adresse}</div>
                            ${phoneNumber}
                            ${webSite}
                            ${
                              response.data.description
                                ? `
                                <div class="popup__category" style="margin-bottom: 20px;">
                                  <div class="popup__description-type popup__description">
                                    Description :
                                  </div>
                                  <div style="font-weight: 500; text-align: end" class="popup__description">
                                    ${response.data.description}
                                  </div>
                                </div>`
                                : ''
                            }
                          </div>`;
            }
            if (window.innerWidth > 750) {
              marker._popup.setHTML(popupBody);
              marker._popup._update();
            } else {
              this._mapStore.setMobilePopup(popupBody);
            }
          });
        }
      });
    };
  }

  private _setMarker() {
    return (marker: Marker) => this._mapStore.addMarker({ marker, type: this._widgetType });
  }

  private static isPoint(point: Point | GeoJSONObject): point is Point {
    return point.type === 'Point';
  }
}
